import React, { useEffect, useMemo, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import { api, todayISO } from "../lib/api";
import { ClipboardList, Plus, Trash2, Pencil, CheckCircle2, RotateCcw, AlertTriangle, Wrench } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

const blank = { equipment_id: "", business_id: "farm", title: "", description: "", priority: "normal", due_date: "" };

const BUSINESS_OPTIONS = [
  { value: "farm", label: "Farm" },
  { value: "jk_repair", label: "J&K Repair" },
  { value: "jk_trucking", label: "J&K Trucking" },
];

export default function Maintenance() {
  const [tasks, setTasks] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [completing, setCompleting] = useState(null);
  const [completeNotes, setCompleteNotes] = useState("");
  const [completeDate, setCompleteDate] = useState(todayISO());

  const load = async () => {
    const [t, e] = await Promise.all([
      api.get(`/maintenance-tasks`),
      api.get(`/equipment`),
    ]);
    setTasks(t.data);
    setEquipment(e.data);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!filter) return tasks;
    return tasks.filter((t) => t.equipment_id === filter);
  }, [tasks, filter]);

  const openTasks = filtered.filter((t) => t.status === "open");
  const doneTasks = filtered.filter((t) => t.status === "done");

  // Overdue marker
  const today = todayISO();
  const isOverdue = (t) => t.status === "open" && t.due_date && t.due_date < today;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id) {
      toast.error("Pick a piece of equipment");
      return;
    }
    try {
      if (editing) {
        await api.put(`/maintenance-tasks/${editing.id}`, form);
        toast.success("Task updated");
      } else {
        await api.post(`/maintenance-tasks`, form);
        toast.success("Task added");
      }
      setOpen(false);
      setEditing(null);
      setForm(blank);
      load();
    } catch {
      toast.error("Could not save task");
    }
  };

  const onEdit = (t) => {
    setEditing(t);
    setForm({
      equipment_id: t.equipment_id,
      business_id: t.business_id || "farm",
      title: t.title,
      description: t.description || "",
      priority: t.priority,
      due_date: t.due_date || "",
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/maintenance-tasks/${id}`);
    toast.success("Task deleted");
    load();
  };

  const startComplete = (t) => {
    setCompleting(t);
    setCompleteNotes("");
    setCompleteDate(todayISO());
  };

  const confirmComplete = async () => {
    try {
      await api.post(`/maintenance-tasks/${completing.id}/complete`, {
        completion_notes: completeNotes,
        completed_at: completeDate,
      });
      toast.success("Marked done");
      setCompleting(null);
      load();
    } catch {
      toast.error("Could not mark done");
    }
  };

  const reopen = async (t) => {
    await api.post(`/maintenance-tasks/${t.id}/reopen`);
    toast.success("Re-opened");
    load();
  };

  return (
    <div>
      <TopBar
        title="Maintenance To-Do"
        subtitle="Ad-hoc tasks for your equipment. Log it, knock it out, jot a note when you do."
        actions={
          <div className="flex flex-wrap items-center gap-2 mt-6">
            <button
              data-testid="add-task-btn"
              onClick={() => { setEditing(null); setForm({ ...blank, equipment_id: equipment[0]?.id || "" }); setOpen(true); }}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#A03B1F] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Task
            </button>
          </div>
        }
      />

      {/* Stats + filter */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="maintenance-stats">
        <StatBox label="Open" value={openTasks.length} tone="warning" />
        <StatBox label="Overdue" value={openTasks.filter(isOverdue).length} tone="danger" />
        <StatBox label="Completed" value={doneTasks.length} tone="positive" />
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-1.5">Filter by Equipment</div>
          <select
            data-testid="task-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
          >
            <option value="">All equipment</option>
            {equipment.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
      </div>

      {/* Open tasks */}
      <Section title="Open" count={openTasks.length} testid="section-open">
        {openTasks.length === 0 ? (
          <EmptyState icon={ClipboardList} title="Nothing on the list" hint="Log something that needs doing when it pops into your head." testid="empty-open-tasks" />
        ) : (
          <div className="space-y-2" data-testid="open-tasks-list">
            {openTasks.map((t) => (
              <TaskRow key={t.id} t={t} isOverdue={isOverdue(t)} onEdit={onEdit} onDelete={onDelete} onComplete={startComplete} />
            ))}
          </div>
        )}
      </Section>

      {/* Done tasks */}
      {doneTasks.length > 0 && (
        <Section title="Completed" count={doneTasks.length} testid="section-done">
          <div className="space-y-2" data-testid="done-tasks-list">
            {doneTasks.map((t) => (
              <TaskRow key={t.id} t={t} done onReopen={reopen} onDelete={onDelete} />
            ))}
          </div>
        </Section>
      )}

      {/* New / edit task modal */}
      {open && (
        <Modal title={editing ? "Edit Task" : "New Maintenance Task"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="task-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Equipment" required>
                <select
                  data-testid="task-equipment-select"
                  required
                  value={form.equipment_id}
                  onChange={(e) => setForm({ ...form, equipment_id: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                >
                  <option value="">— Select equipment —</option>
                  {equipment.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </Field>
              <Field label="Priority">
                <select
                  data-testid="task-priority-select"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                >
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="For business">
              <select
                data-testid="task-business-select"
                value={form.business_id}
                onChange={(e) => setForm({ ...form, business_id: e.target.value })}
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              >
                {BUSINESS_OPTIONS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <div className="text-[10px] text-[#66645E] mt-1">Tags this task to the right business — drives the color on the Calendar and lets you filter by tab.</div>
            </Field>
            <Field label="Task" required>
              <input
                data-testid="task-title-input"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Replace front-end seat / Patch loader hose leak / Grease combine"
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Notes / Detail">
              <textarea
                data-testid="task-desc-input"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional — anything you want to remember when you tackle it"
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Due Date (optional)">
              <input
                data-testid="task-due-input"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="task-cancel-btn" onClick={() => setOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="task-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Task</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Complete task modal */}
      {completing && (
        <Modal title={`Mark Done: ${completing.title}`} onClose={() => setCompleting(null)}>
          <div className="space-y-4" data-testid="complete-task-form">
            <Field label="Date Completed">
              <input
                data-testid="complete-date-input"
                type="date"
                value={completeDate}
                onChange={(e) => setCompleteDate(e.target.value)}
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Notes (optional)">
              <textarea
                data-testid="complete-notes-input"
                rows={4}
                value={completeNotes}
                onChange={(e) => setCompleteNotes(e.target.value)}
                placeholder="What you did, parts used, anything to remember"
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" data-testid="complete-cancel-btn" onClick={() => setCompleting(null)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="button" data-testid="complete-confirm-btn" onClick={confirmComplete} className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128] flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Mark Done
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatBox({ label, value, tone }) {
  const color = tone === "danger" ? "text-[#BC4727]" : tone === "warning" ? "text-[#1F1E1B]" : tone === "positive" ? "text-[#1A4135]" : "text-[#1F1E1B]";
  return (
    <div className="bg-white border border-[#1F1E1B] p-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">{label}</div>
      <div className={`font-display text-4xl font-black tabular mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Section({ title, count, testid, children }) {
  return (
    <div className="mb-8" data-testid={testid}>
      <div className="flex items-baseline justify-between border-b border-[#1F1E1B] pb-2 mb-3">
        <h3 className="font-display text-lg font-bold uppercase tracking-wider">{title}</h3>
        <div className="text-[10px] font-bold tracking-[0.18em] text-[#66645E]">{count} {count === 1 ? "task" : "tasks"}</div>
      </div>
      {children}
    </div>
  );
}

function TaskRow({ t, done, isOverdue, onEdit, onDelete, onComplete, onReopen }) {
  const priorityStyle = t.priority === "high" ? "bg-[#BC4727] text-white" : t.priority === "low" ? "bg-[#F5F4F0] text-[#66645E]" : "bg-[#1F1E1B] text-white";
  return (
    <div className={`bg-white border border-[#1F1E1B] p-4 ${done ? "opacity-70" : ""}`} data-testid={`task-row-${t.id}`}>
      <div className="flex items-start gap-4">
        {/* Checkbox-style toggle */}
        {done ? (
          <button
            data-testid={`reopen-task-${t.id}`}
            onClick={() => onReopen(t)}
            title="Reopen task"
            className="flex-shrink-0 w-7 h-7 border-2 border-[#1A4135] bg-[#1A4135] text-white flex items-center justify-center hover:bg-white hover:text-[#1A4135] transition-colors"
          >
            <CheckCircle2 className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid={`complete-task-${t.id}`}
            onClick={() => onComplete(t)}
            title="Mark done"
            className="flex-shrink-0 w-7 h-7 border-2 border-[#1F1E1B] bg-white hover:bg-[#1A4135] hover:border-[#1A4135] hover:text-white transition-colors"
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2 mb-1">
            <span className={`text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 ${priorityStyle}`}>{t.priority}</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] flex items-center gap-1">
              <Wrench className="w-3 h-3" /> {t.equipment_name}
            </span>
            {isOverdue && (
              <span className="text-[10px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 bg-[#BC4727] text-white flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> Overdue
              </span>
            )}
            {t.due_date && !done && !isOverdue && (
              <span className="text-[10px] text-[#66645E] tabular">Due {t.due_date}</span>
            )}
          </div>
          <div className={`font-semibold text-base ${done ? "line-through text-[#66645E]" : ""}`}>{t.title}</div>
          {t.description && <div className="text-sm text-[#66645E] mt-1">{t.description}</div>}
          {done && (
            <div className="mt-2 pt-2 border-t border-dashed border-[#D2CEC1] text-sm">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#1A4135]">Done {t.completed_at}</span>
              {t.completion_notes && <div className="text-[#66645E] mt-1 italic">{t.completion_notes}</div>}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-1.5 flex-shrink-0">
          {!done && (
            <button data-testid={`edit-task-${t.id}`} onClick={() => onEdit(t)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
          )}
          {done && (
            <button data-testid={`reopen-action-${t.id}`} onClick={() => onReopen(t)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white" title="Reopen"><RotateCcw className="w-3 h-3" /></button>
          )}
          <button data-testid={`delete-task-${t.id}`} onClick={() => onDelete(t.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
    </div>
  );
}
